import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Banner,
  BannerAction,
  BannerActions,
  BannerDescription,
  BannerTitle,
} from "./Banner";
import { postApiCall } from "../service/PostApiCall";
import { USER_NOTIFICATION_READ } from "../service/apiUrl";
import {
  NOTIFICATIONS_EVENT,
  latestNotifications,
  refreshNotifications,
} from "../utility/notificationsFeed";

/* ============================================================================
   "You've been added to <project>" — an employee's unread `project`
   notifications, as a banner at the top of the shell's panel.

   The list comes from the sidebar's notification poll (notificationsFeed), so
   there is no second poll. Opening the project clears it server-side
   (/user-api/my-project-view marks it read); the ✕ marks it read here. Either
   way it disappears from the bell as well. Hidden while the user is already
   on that project's page.
   ========================================================================== */
const isUnreadProject = (item) =>
  item?.type === "project" && String(item?.isRead) !== "1";

const NewProjectBanner = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const token = useSelector((state) => state.signinReducers)?.data?.access_token;
  const [items, setItems] = useState(() => latestNotifications().filter(isUnreadProject));
  // hidden at once on ✕, before the read call and the next poll catch up
  const [dismissed, setDismissed] = useState([]);

  useEffect(() => {
    const onList = (event) => setItems((event.detail || []).filter(isUnreadProject));
    window.addEventListener(NOTIFICATIONS_EVENT, onList);
    return () => window.removeEventListener(NOTIFICATIONS_EVENT, onList);
  }, []);

  const visible = items.filter(
    (item) =>
      !dismissed.includes(item.id) && pathname !== `/my-projects/${item.doc_id}`,
  );
  // one project per banner row: newest first, the rest wait their turn
  const projects = [...new Map(visible.map((item) => [item.doc_id, item])).values()];
  const first = projects[0];

  const markRead = (projectId) => {
    const ids = visible.filter((item) => item.doc_id === projectId).map((item) => item.id);
    setDismissed((prev) => [...prev, ...ids]);
    Promise.all(ids.map((id) => postApiCall({ id }, token, USER_NOTIFICATION_READ)))
      .catch((error) => console.error("PROJECT_BANNER", error))
      .finally(refreshNotifications);
  };

  const others = projects.length - 1;

  return (
    <Banner
      open={Boolean(first)}
      status="info"
      contrast="high"
      dismissible
      onDismiss={() => first && markRead(first.doc_id)}
      className="app-banner"
    >
      <BannerTitle>{first?.title?.replace(/^Added to /, "You've been added to ")}</BannerTitle>
      <BannerDescription>
        {first?.description}
        {others > 0 ? ` · ${others} more new project${others > 1 ? "s" : ""}` : ""}
      </BannerDescription>
      <BannerActions>
        <BannerAction
          variant="primary"
          onClick={() => first && navigate(`/my-projects/${first.doc_id}`)}
        >
          Open project
        </BannerAction>
        {others > 0 ? (
          <BannerAction onClick={() => navigate("/my-projects")}>
            All my projects
          </BannerAction>
        ) : null}
      </BannerActions>
    </Banner>
  );
};

export default NewProjectBanner;
